/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { array, bool } from 'prop-types';
import imagesLoaded from 'imagesloaded';
import Masonry from 'masonry-layout';
import classNames from 'clsx';

import ImageModal from 'components/ImageModal/ImageModal';
import Spinner from 'components/Spinner/Spinner';

import Image from './Image/Image';

import styles from './gallery.scss';

/**
 * This is the Gallery component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class Gallery extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    images: array.isRequired,
    lightboxImages: array,
    fillContainer: bool,
  };

  static defaultProps = {
    lightboxImages: [],
    fillContainer: false,
  };

  /**
   * Create Gallery.
   */
  constructor(props) {
    super(props);

    this.masonry = null;
    this.imageLoader = null;

    this.state = {
      imagesLoaded: false,
      currentModalIndex: null,
    };
  }

  componentDidMount() {
    const { fillContainer } = this.props;

    if (this.grid && !fillContainer) {
      this.masonry = new Masonry(this.grid, {
        gutter: 0,
        percentPosition: true,
      });

      this.imageLoader = new imagesLoaded(this.grid, () => {
        if (this.masonry) {
          this.setState({ imagesLoaded: true }, () => this.masonry.layout());
        }
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { images: nextImages, fillContainer } = this.props;
    const { images } = prevProps;

    if (nextImages.length !== images.length && !fillContainer && this.masonry) {
      this.masonry.layout();
    }
  }

  componentWillUnmount() {
    if (this.masonry) this.masonry.destroy();

    this.imageLoader = null;
  }

  onClick = index => this.setState({ currentModalIndex: index });

  closeModal = () => this.setState({ currentModalIndex: null });

  /**
   * Render Gallery.
   *
   * @return {Object}
   */
  render() {
    const { currentModalIndex, imagesLoaded } = this.state;
    const { images, lightboxImages, fillContainer } = this.props;
    const lightbox = lightboxImages.length > 0 ? lightboxImages : images;

    // Make imags in sequential array.
    const lightboxArray = lightbox.filter(() => true);

    const hasImageLoader = !fillContainer;

    if (images.length <= 0) return null;

    return (
      <section
        className={classNames(styles.wrapper, {
          [styles.fill]: fillContainer,
        })}
      >
        {hasImageLoader && !imagesLoaded && (
          <div className={styles.spinner}>
            <Spinner medium />
          </div>
        )}

        <div
          className={styles.grid}
          ref={ref => {
            this.grid = ref;
          }}
        >
          {images.map(({ picture_id, medium_url, index }) => {
            return (
              <div
                key={picture_id}
                className={classNames(styles.gridItem, {
                  [styles.fill]: fillContainer,
                })}
              >
                <Image
                  url={medium_url}
                  fill={fillContainer}
                  hidden={fillContainer ? false : !imagesLoaded}
                  onClick={() => this.onClick(index)}
                />
              </div>
            );
          })}
        </div>

        {currentModalIndex !== null && (
          <ImageModal
            images={lightboxArray}
            index={currentModalIndex}
            onClose={this.closeModal}
          />
        )}
      </section>
    );
  }
}
